/**
 * Formatting bar on a text selection (bold, colours, alignment, …) and a bar
 * above a table while the cursor is in it (rows, columns, header).
 */
import type { Editor } from '@tiptap/core';
import { NodeSelection } from '@tiptap/pm/state';
import { CellSelection } from '@tiptap/pm/tables';
import { useEditorState } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import {
  Baseline,
  BetweenHorizontalEnd,
  BetweenHorizontalStart,
  BetweenVerticalEnd,
  BetweenVerticalStart,
  Bold,
  Code,
  Eraser,
  Grid2x2X,
  Highlighter,
  Italic,
  Link,
  Link2Off,
  Palette,
  Sparkles,
  PanelTop,
  Strikethrough,
  TableColumnsSplit,
  TableRowsSplit,
  TextAlignCenter,
  TextAlignEnd,
  TextAlignStart,
  Underline,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { HIGHLIGHT_COLORS, TEXT_COLORS } from '../schema';
import { useHost } from './host';

/** "#ffd400" → "rgba(255, 212, 0, 0.35)". */
function translucent(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, 0.35)`;
}

/** Touch screens show their own selection menu above the text: put ours below. */
const coarse = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;

function Button({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`nx-bar-button${active ? ' is-active' : ''}`}
      aria-label={label}
      aria-pressed={active}
      title={label}
      // Keep the selection in the editor.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

const BLOCK_TYPES = ['text', 'h1', 'h2', 'h3', 'bullets', 'numbered', 'tasks'] as const;

function setBlock(editor: Editor, type: (typeof BLOCK_TYPES)[number]) {
  const c = editor.chain().focus();
  if (type === 'text') c.setParagraph().run();
  else if (type === 'bullets') c.toggleBulletList().run();
  else if (type === 'numbered') c.toggleOrderedList().run();
  else if (type === 'tasks') c.toggleTaskList().run();
  else c.setHeading({ level: Number(type.slice(1)) as 1 | 2 | 3 }).run();
}

export function FormatBar({ editor }: { editor: Editor }) {
  const host = useHost();
  const [panel, setPanel] = useState<null | 'color' | 'link'>(null);
  const [href, setHref] = useState('');
  const s = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      underline: e.isActive('underline'),
      strike: e.isActive('strike'),
      code: e.isActive('code'),
      link: e.isActive('link'),
      color: (e.getAttributes('textStyle').color as string | undefined) ?? null,
      highlight: e.isActive('highlight'),
      align: (['center', 'right'] as const).find((a) => e.isActive({ textAlign: a })) ?? 'left',
      block: e.isActive('taskList')
        ? 'tasks'
        : e.isActive('bulletList')
          ? 'bullets'
          : e.isActive('orderedList')
            ? 'numbered'
            : ([1, 2, 3] as const).find((l) => e.isActive('heading', { level: l }))
              ? `h${([1, 2, 3] as const).find((l) => e.isActive('heading', { level: l }))}`
              : 'text',
    }),
  });
  const t = (k: string) => host.t(`editor.format.${k}`);
  const chain = () => editor.chain().focus();

  return (
    <BubbleMenu
      editor={editor}
      pluginKey="nexusFormatBar"
      className="nx-bar"
      options={{ placement: coarse() ? 'bottom' : 'top', offset: 8, flip: true, shift: true }}
      shouldShow={({ editor: e, state, from, to }) =>
        e.isEditable &&
        from !== to &&
        !(state.selection instanceof NodeSelection) &&
        !(state.selection instanceof CellSelection) &&
        !e.isActive('codeBlock')
      }
    >
      {panel === 'link' ? (
        <form
          className="nx-bar-row"
          onSubmit={(e) => {
            e.preventDefault();
            const url = href.trim();
            if (url) chain().extendMarkRange('link').setLink({ href: url }).run();
            setPanel(null);
          }}
        >
          <input
            className="nx-bar-input"
            autoFocus
            type="url"
            placeholder="https://"
            aria-label={t('linkUrl')}
            value={href}
            onChange={(e) => setHref(e.target.value)}
            onKeyDown={(e) => e.key === 'Escape' && setPanel(null)}
          />
          <button type="submit" className="nx-bar-button nx-bar-text">
            {t('linkSave')}
          </button>
        </form>
      ) : (
        <div className="nx-bar-row">
          {host.aiWrite && (
            <>
              <button
                type="button"
                className="nx-bar-button nx-bar-text nx-bar-ai"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => host.aiWrite?.(editor)}
              >
                <Sparkles size={15} />
                {t('ai')}
              </button>
              <span className="nx-bar-sep" />
            </>
          )}
          <select
            className="nx-bar-select"
            aria-label={t('turnInto')}
            value={s.block}
            onChange={(e) => setBlock(editor, e.target.value as (typeof BLOCK_TYPES)[number])}
          >
            {BLOCK_TYPES.map((b) => (
              <option key={b} value={b}>
                {host.t(`editor.slash.${b}`)}
              </option>
            ))}
          </select>
          <span className="nx-bar-sep" />
          <Button label={t('bold')} active={s.bold} onClick={() => chain().toggleBold().run()}>
            <Bold size={15} />
          </Button>
          <Button
            label={t('italic')}
            active={s.italic}
            onClick={() => chain().toggleItalic().run()}
          >
            <Italic size={15} />
          </Button>
          <Button
            label={t('underline')}
            active={s.underline}
            onClick={() => chain().toggleUnderline().run()}
          >
            <Underline size={15} />
          </Button>
          <Button
            label={t('strike')}
            active={s.strike}
            onClick={() => chain().toggleStrike().run()}
          >
            <Strikethrough size={15} />
          </Button>
          <Button label={t('code')} active={s.code} onClick={() => chain().toggleCode().run()}>
            <Code size={15} />
          </Button>
          {s.link ? (
            <Button
              label={t('unlink')}
              active
              onClick={() => chain().extendMarkRange('link').unsetLink().run()}
            >
              <Link2Off size={15} />
            </Button>
          ) : (
            <Button
              label={t('link')}
              onClick={() => {
                setHref('');
                setPanel('link');
              }}
            >
              <Link size={15} />
            </Button>
          )}
          <span className="nx-bar-sep" />
          <Button
            label={t('colors')}
            active={panel === 'color' || !!s.color || s.highlight}
            onClick={() => setPanel(panel === 'color' ? null : 'color')}
          >
            <Baseline size={15} style={s.color ? { color: s.color } : undefined} />
          </Button>
          <span className="nx-bar-sep" />
          {(
            [
              ['left', <TextAlignStart key="l" size={15} />],
              ['center', <TextAlignCenter key="c" size={15} />],
              ['right', <TextAlignEnd key="r" size={15} />],
            ] as const
          ).map(([a, icon]) => (
            <Button
              key={a}
              label={t(`align_${a}`)}
              active={s.align === a}
              onClick={() =>
                a === 'left' ? chain().unsetTextAlign().run() : chain().setTextAlign(a).run()
              }
            >
              {icon}
            </Button>
          ))}
        </div>
      )}
      {panel === 'color' && (
        <div className="nx-bar-colors">
          <div className="nx-bar-row" role="group" aria-label={t('textColor')}>
            <Baseline size={14} className="nx-muted" />
            {TEXT_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className={`nx-swatch${s.color === c ? ' is-active' : ''}`}
                style={{ color: c }}
                aria-label={`${t('textColor')} ${c}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => chain().setColor(c).run()}
              >
                A
              </button>
            ))}
            <label
              className="nx-swatch nx-swatch-custom"
              title={t('customColor')}
              onMouseDown={(e) => e.preventDefault()}
            >
              <Palette size={13} />
              <input
                type="color"
                aria-label={t('customColor')}
                value={s.color && s.color.startsWith('#') ? s.color : '#c4521c'}
                onChange={(e) => chain().setColor(e.target.value).run()}
              />
            </label>
          </div>
          <div className="nx-bar-row" role="group" aria-label={t('highlight')}>
            <Highlighter size={14} className="nx-muted" />
            {HIGHLIGHT_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className="nx-swatch"
                style={{ background: c }}
                aria-label={`${t('highlight')} ${c}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => chain().setHighlight({ color: c }).run()}
              />
            ))}
            <label
              className="nx-swatch nx-swatch-custom"
              title={t('customHighlight')}
              onMouseDown={(e) => e.preventDefault()}
            >
              <Palette size={13} />
              <input
                type="color"
                aria-label={t('customHighlight')}
                defaultValue="#ffd400"
                // A see-through version, so the text stays readable in light and dark.
                onChange={(e) =>
                  chain()
                    .setHighlight({ color: translucent(e.target.value) })
                    .run()
                }
              />
            </label>
          </div>
          <button
            type="button"
            className="nx-bar-button nx-bar-text"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              chain().unsetColor().unsetHighlight().run();
              setPanel(null);
            }}
          >
            <Eraser size={14} /> {t('clearColors')}
          </button>
        </div>
      )}
    </BubbleMenu>
  );
}

/** The table around the cursor, for placing the bar above it. */
function tableElement(editor: Editor): HTMLElement | null {
  const { $from } = editor.state.selection;
  for (let d = $from.depth; d > 0; d--) {
    if ($from.node(d).type.name === 'table') {
      const dom = editor.view.nodeDOM($from.before(d)) as HTMLElement | null;
      return dom?.querySelector('table') ?? dom;
    }
  }
  return null;
}

export function TableBar({ editor }: { editor: Editor }) {
  const host = useHost();
  const t = (k: string) => host.t(`editor.table.${k}`);
  const chain = () => editor.chain().focus();
  const actions: { id: string; icon: ReactNode; run: () => void }[] = [
    {
      id: 'rowAbove',
      icon: <BetweenHorizontalStart size={15} />,
      run: () => chain().addRowBefore().run(),
    },
    {
      id: 'rowBelow',
      icon: <BetweenHorizontalEnd size={15} />,
      run: () => chain().addRowAfter().run(),
    },
    {
      id: 'colLeft',
      icon: <BetweenVerticalStart size={15} />,
      run: () => chain().addColumnBefore().run(),
    },
    {
      id: 'colRight',
      icon: <BetweenVerticalEnd size={15} />,
      run: () => chain().addColumnAfter().run(),
    },
    { id: 'deleteRow', icon: <TableRowsSplit size={15} />, run: () => chain().deleteRow().run() },
    {
      id: 'deleteCol',
      icon: <TableColumnsSplit size={15} />,
      run: () => chain().deleteColumn().run(),
    },
    { id: 'header', icon: <PanelTop size={15} />, run: () => chain().toggleHeaderRow().run() },
    { id: 'delete', icon: <Grid2x2X size={15} />, run: () => chain().deleteTable().run() },
  ];
  return (
    <BubbleMenu
      editor={editor}
      pluginKey="nexusTableBar"
      className="nx-bar"
      options={{ placement: 'top-start', offset: 6, flip: false, shift: true }}
      getReferencedVirtualElement={() => {
        const el = tableElement(editor);
        return el ? { getBoundingClientRect: () => el.getBoundingClientRect() } : null;
      }}
      shouldShow={({ editor: e, state }) =>
        e.isEditable &&
        e.isActive('table') &&
        (state.selection.empty || state.selection instanceof CellSelection)
      }
    >
      <div className="nx-bar-row" role="toolbar" aria-label={t('label')}>
        {actions.map((a) => (
          <Button key={a.id} label={t(a.id)} onClick={a.run}>
            {a.icon}
          </Button>
        ))}
      </div>
    </BubbleMenu>
  );
}
